import React, { ReactNode, useContext, useRef } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  SensorDescriptor,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaSort } from 'react-icons/fa';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import {
  countActiveFilters,
  getDistinctColumnValues,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/column-filter';
import { ColumnFilterMenu } from '@/features/ai-agents/chat/MarkdownTableVisualization/ColumnFilterMenu';
import { AccountContext } from '@/features/account-context';
import { ColumnTooltipIcon } from '@/features/ai-agents/chat/MarkdownTableVisualization/ColumnTooltipIcon';
import { HorizontalScrollFrame } from '@/features/ai-agents/chat/MarkdownTableVisualization/HorizontalScrollFrame';
import { renderLinkableCellValue } from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
import {
  CellBadge,
  ColumnResizer,
  FilterSummary,
  HeaderLabel,
  HeaderRow,
  SortIndicator,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import {
  ColumnFilter,
  ColumnFilters,
  ParsedTable,
  RenderedTable,
  SortedTableRow,
  SortDirection,
  TableSort,
  TooltipTextResolver,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  findNumericColumnIndexes,
  formatColumnHeader,
  formatNumericCellValue,
  reorderColumns,
  splitNumberWithBadge,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface TableViewProps {
  columnFilters: ColumnFilters;
  currentSort: TableSort | null;
  getColumnWidthStyle: (
    columnIndex: number,
  ) => { maxWidth: string; minWidth: string; width: string } | undefined;
  getTooltip?: TooltipTextResolver;
  numericColumnIndexes?: number[];
  onClearFilters: () => void;
  onColumnOrderChange: (columnOrder: number[]) => void;
  onFilterChange: (columnIndex: number, filter: ColumnFilter | null) => void;
  onSort: (columnIndex: number) => void;
  orderedColumnIndexes: number[];
  renderedTable: RenderedTable;
  sensors: SensorDescriptor<any>[];
  sortedRows: SortedTableRow[];
  startResizing: (
    columnIndex: number,
    startWidth: number,
    startX: number,
  ) => void;
  table: ParsedTable;
}

interface SortableTableHeaderProps {
  align?: 'start' | 'end';
  children: ReactNode;
  columnIndex: number;
  direction?: SortDirection;
  filterMenu?: ReactNode;
  isFrozen?: boolean;
  label: string;
  onResizeStart: (
    columnIndex: number,
    startX: number,
    thElement: HTMLElement | null,
  ) => void;
  onSort: () => void;
  tooltipText: string | null;
  widthStyle?: { maxWidth: string; minWidth: string; width: string };
}

export function TableView({
  columnFilters,
  currentSort,
  getColumnWidthStyle,
  getTooltip,
  numericColumnIndexes,
  onClearFilters,
  onColumnOrderChange,
  onFilterChange,
  onSort,
  orderedColumnIndexes,
  renderedTable,
  sensors,
  sortedRows,
  startResizing,
  table,
}: TableViewProps) {
  const { account } = useContext(AccountContext);
  const siteDomain = account?.store?.domain;
  const resolvedNumericColumnIndexes =
    numericColumnIndexes ?? findNumericColumnIndexes(table);

  const activeFilterCount = countActiveFilters(columnFilters);

  const handleColumnDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    onColumnOrderChange(
      reorderColumns(orderedColumnIndexes, Number(active.id), Number(over.id)),
    );
  };

  const handleResizeStart = (
    columnIndex: number,
    startX: number,
    thElement: HTMLElement | null,
  ) => {
    const startWidth = thElement?.getBoundingClientRect().width ?? 0;
    startResizing(columnIndex, startWidth, startX);
  };

  return (
    <>
      {activeFilterCount > 0 && (
        <FilterSummary>
          <span>
            {sortedRows.length} of {table.rows.length} rows shown
          </span>
          <button type="button" onClick={onClearFilters}>
            Clear all filters
          </button>
        </FilterSummary>
      )}
      <HorizontalScrollFrame>
        {(scrollRef) => (
          <TableWrapper ref={scrollRef} className="table-wrapper">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleColumnDragEnd}
            >
              <Table>
                <Thead>
                  <Tr>
                    <SortableContext
                      items={orderedColumnIndexes}
                      strategy={horizontalListSortingStrategy}
                    >
                      {orderedColumnIndexes.map((columnIndex, position) => {
                        const isNumeric =
                          resolvedNumericColumnIndexes.includes(columnIndex);
                        const align = isNumeric ? `end` : `start`;
                        const headerContent =
                          renderedTable.headers[columnIndex] ??
                          table.headers[columnIndex];
                        const formattedHeader =
                          typeof headerContent === `string`
                            ? formatColumnHeader(headerContent)
                            : headerContent;

                        return (
                          <SortableTableHeader
                            key={columnIndex}
                            align={align}
                            columnIndex={columnIndex}
                            isFrozen={position === 0}
                            label={table.headers[columnIndex]}
                            direction={
                              currentSort?.columnIndex === columnIndex
                                ? currentSort.direction
                                : undefined
                            }
                            filterMenu={
                              <ColumnFilterMenu
                                filter={columnFilters[columnIndex]}
                                isNumeric={isNumeric}
                                label={table.headers[columnIndex]}
                                onChange={(filter) =>
                                  onFilterChange(columnIndex, filter)
                                }
                                values={getDistinctColumnValues(
                                  table,
                                  columnIndex,
                                  isNumeric,
                                )}
                              />
                            }
                            onResizeStart={handleResizeStart}
                            onSort={() => onSort(columnIndex)}
                            tooltipText={
                              getTooltip?.(table.headers[columnIndex]) ?? null
                            }
                            widthStyle={getColumnWidthStyle(columnIndex)}
                          >
                            {formattedHeader}
                          </SortableTableHeader>
                        );
                      })}
                    </SortableContext>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedRows.map((row) => (
                    <Tr key={row.originalIndex}>
                      {orderedColumnIndexes.map((columnIndex, position) => {
                        const isNumeric =
                          resolvedNumericColumnIndexes.includes(columnIndex);
                        const align = isNumeric ? `end` : `start`;
                        const cellContent =
                          renderedTable.rows[row.originalIndex]?.[
                            columnIndex
                          ] ?? row.values[columnIndex];

                        return (
                          <Td
                            key={columnIndex}
                            $align={align}
                            $isFrozen={position === 0}
                            style={getColumnWidthStyle(columnIndex)}
                          >
                            {isNumeric && typeof cellContent === `string`
                              ? renderNumericCell(cellContent)
                              : renderLinkableCellValue(
                                  cellContent,
                                  siteDomain,
                                )}
                          </Td>
                        );
                      })}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </DndContext>
          </TableWrapper>
        )}
      </HorizontalScrollFrame>
    </>
  );
}

function renderNumericCell(value: string) {
  const withBadge = splitNumberWithBadge(value);

  if (!withBadge) {
    return formatNumericCellValue(value);
  }

  return (
    <>
      {formatNumericCellValue(withBadge.number)}
      <CellBadge>{withBadge.badge}</CellBadge>
    </>
  );
}

function SortableTableHeader({
  align = `start`,
  children,
  columnIndex,
  direction,
  filterMenu,
  isFrozen,
  label,
  onResizeStart,
  onSort,
  tooltipText,
  widthStyle,
}: SortableTableHeaderProps) {
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: columnIndex });

  const setRefs = (node: HTMLTableCellElement | null) => {
    thRef.current = node;
    setNodeRef(node);
  };

  const sortIndicator = (
    <SortIndicator aria-hidden $isSorted={Boolean(direction)}>
      {direction === `ascending` && <FiArrowUp />}
      {direction === `descending` && <FiArrowDown />}
      {!direction && <FaSort />}
    </SortIndicator>
  );
  const headerLabel = <HeaderLabel $align={align}>{children}</HeaderLabel>;
  const tooltipIcon = <ColumnTooltipIcon text={tooltipText} />;

  return (
    <Th
      ref={setRefs}
      $align={align}
      $isFrozen={isFrozen}
      style={{
        ...widthStyle,
        opacity: isDragging ? 0.6 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      aria-sort={direction ?? `none`}
    >
      <HeaderRow
        $align={align}
        role="button"
        tabIndex={0}
        onClick={onSort}
        onKeyDown={(event) => {
          if (event.key === `Enter` || event.key === ` `) {
            event.preventDefault();
            onSort();
          }
        }}
        {...attributes}
        {...listeners}
      >
        {align === `end` ? (
          <>
            {filterMenu}
            {sortIndicator}
            {headerLabel}
            {tooltipIcon}
          </>
        ) : (
          <>
            {headerLabel}
            {tooltipIcon}
            {sortIndicator}
            {filterMenu}
          </>
        )}
      </HeaderRow>
      <ColumnResizer
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label} column`}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onResizeStart(columnIndex, event.clientX, thRef.current);
        }}
      />
    </Th>
  );
}

export function MarkdownTableHead({ children }: { children: ReactNode }) {
  return <Thead>{children}</Thead>;
}

export function MarkdownTableBody({ children }: { children: ReactNode }) {
  return <Tbody>{children}</Tbody>;
}

export function MarkdownTableRow({ children }: { children: ReactNode }) {
  return <Tr>{children}</Tr>;
}

export function MarkdownTableHeader({
  align,
  children,
}: {
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  return <Th $align={align}>{children}</Th>;
}

export function MarkdownTableCell({
  align,
  children,
}: {
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  return <Td $align={align}>{children}</Td>;
}
